import i18n from "@/lib/i18n";

export type LegalEntityKind =
  | "housing_community"
  | "housing_cooperative"
  | "property_manager"
  | "company";

const KIND_I18N_KEYS: Record<LegalEntityKind, string> = {
  housing_community: "admin.legalEntity.kind.housingCommunity",
  housing_cooperative: "admin.legalEntity.kind.housingCooperative",
  property_manager: "admin.legalEntity.kind.propertyManager",
  company: "admin.legalEntity.kind.company",
};

export const LEGAL_ENTITY_KIND_LABELS: Record<LegalEntityKind, string> = {
  get housing_community() {
    return i18n.t(KIND_I18N_KEYS.housing_community);
  },
  get housing_cooperative() {
    return i18n.t(KIND_I18N_KEYS.housing_cooperative);
  },
  get property_manager() {
    return i18n.t(KIND_I18N_KEYS.property_manager);
  },
  get company() {
    return i18n.t(KIND_I18N_KEYS.company);
  },
};

export const HOUSING_KINDS: LegalEntityKind[] = [
  "housing_community",
  "housing_cooperative",
];

export const COMPANY_KINDS: LegalEntityKind[] = ["company", "property_manager"];

export type AddressOwner = {
  ownerNip?: string;
  ownerName?: string;
};

export function legalEntityErrorMessage(
  code: string,
  owner?: AddressOwner | null,
): string {
  switch (code) {
    case "invalid_nip":
      return i18n.t("admin.legalEntity.invalidNip");
    case "not_in_gus":
      return i18n.t("admin.legalEntity.notInGusAdminOnly");
    case "gus_inactive":
      return i18n.t("admin.legalEntity.gusInactiveCannotAdd");
    case "NOT_IN_GUS":
      return i18n.t("admin.legalEntity.notInGus");
    case "GUS_INACTIVE":
      return i18n.t("admin.legalEntity.gusInactive");
    case "GUS_NOT_CONFIGURED":
      return i18n.t("admin.legalEntity.gusNotConfigured");
    case "ADDRESS_OWNED_BY_OTHER_ENTITY": {
      const name = owner?.ownerName?.trim();
      const nip = owner?.ownerNip?.trim();
      if (name && nip) {
        return i18n.t("admin.legalEntity.addressOwned", { name, nip });
      }
      return i18n.t("admin.legalEntity.addressOwnedOther");
    }
    case "LEGAL_ENTITY_INCOMPLETE_DATA":
      return i18n.t("admin.legalEntity.incompleteData");
    case "LEGAL_ENTITY_GUS_STILL_AVAILABLE":
      return i18n.t("admin.legalEntity.gusStillAvailable");
    case "LEGAL_ENTITY_UNVERIFIED_FORBIDDEN":
      return i18n.t("admin.legalEntity.unverifiedForbidden");
    case "LEGAL_ENTITY_VERIFY_PLATFORM_ONLY":
      return i18n.t("admin.legalEntity.verifyPlatformOnly");
    case "LEGAL_ENTITY_ALREADY_VERIFIED":
      return i18n.t("admin.legalEntity.alreadyVerified");
    case "LEGAL_ENTITY_REGON_TAKEN":
      return i18n.t("admin.legalEntity.regonTaken");
    case "LEGAL_ENTITY_INVALID_VERIFICATION_REASON":
      return i18n.t("admin.legalEntity.invalidVerificationReason");
    case "GUS_LOGIN_FAILED":
    case "GUS_FAILED":
      return i18n.t("admin.legalEntity.gusUnavailableQueue");
    case "LEGAL_ENTITY_NOT_ENROLLED":
      return i18n.t("admin.legalEntity.notEnrolled");
    case "BUILDING_ADDRESS_REQUIRED":
      return i18n.t("admin.legalEntity.addressFromPlaces");
    case "DUPLICATE_BUILDING":
      return i18n.t("admin.legalEntity.duplicateBuilding");
    case "COMMUNITY_INACTIVE":
      return i18n.t("admin.legalEntity.communityInactive");
    default:
      if (code.startsWith("GUS_HTTP_")) {
        return i18n.t("admin.legalEntity.gusUnavailableQueue");
      }
      return i18n.t("admin.legalEntity.operationFailed");
  }
}
